/**
 * The doorbell call socket (doorbell live calls spec, "Signalling"):
 * `ws/doorbell/calls/{id}/?token=…`, one consumer for the tablet and the
 * member. Frames are JSON `{"type": <name>, ...fields}` both ways, the same
 * framing as the portal socket.
 */

export type CallState = "ringing" | "connected" | "ended" | "missed" | "declined" | "cancelled";

export type CallEndReason =
  | "hangup_visitor"
  | "hangup_member"
  | "disconnect"
  | "timeout"
  | "all_declined"
  | "cancelled"
  | "no_answer_test";

export type SignalKind = "offer" | "answer" | "candidate";

export type ClientFrame =
  | { type: "accept"; video: boolean }
  | { type: "rejoin" }
  | { type: "signal"; kind: SignalKind; payload: unknown; fresh?: boolean }
  | { type: "media"; muted: boolean; camera: boolean }
  | { type: "hangup" }
  | { type: "pong" };

export type ServerFrame =
  | { type: "state"; state: CallState; answered_by_name: string | null; answered_with_video: boolean | null; you: boolean }
  | { type: "signal"; kind: SignalKind; payload: unknown }
  | { type: "media"; muted: boolean; camera: boolean }
  | { type: "door"; ok: boolean; message: string | null }
  | { type: "ended"; end_reason: CallEndReason }
  | { type: "ping" };

/** Final close codes (every code ≥ 4000 is final; below 4000 is a network drop). */
export const CLOSE_AUTH = 4401;
export const CLOSE_FORBIDDEN = 4403;
export const CLOSE_UNKNOWN_CALL = 4404;
/** Lost the accept (after the actual `state`, with `you: false`), or the call already ended. */
export const CLOSE_TAKEN = 4409;

const CALL_STATES: readonly string[] = ["ringing", "connected", "ended", "missed", "declined", "cancelled"];
const SIGNAL_KINDS: readonly string[] = ["offer", "answer", "candidate"];

export function isCallState(v: unknown): v is CallState {
  return typeof v === "string" && CALL_STATES.includes(v);
}

/** One server frame, or null for anything malformed or unknown (never throws). */
export function parseServerFrame(raw: unknown): ServerFrame | null {
  let msg: unknown;
  try {
    msg = JSON.parse(String(raw));
  } catch {
    return null;
  }
  if (!msg || typeof msg !== "object") return null;
  const m = msg as Record<string, unknown>;
  switch (m.type) {
    case "ping":
      return { type: "ping" };
    case "state":
      if (!isCallState(m.state)) return null;
      return {
        type: "state",
        state: m.state,
        answered_by_name: typeof m.answered_by_name === "string" ? m.answered_by_name : null,
        answered_with_video: typeof m.answered_with_video === "boolean" ? m.answered_with_video : null,
        you: m.you === true,
      };
    case "signal":
      if (typeof m.kind !== "string" || !SIGNAL_KINDS.includes(m.kind)) return null;
      return { type: "signal", kind: m.kind as SignalKind, payload: m.payload };
    case "media":
      return { type: "media", muted: m.muted === true, camera: m.camera === true };
    case "door":
      return { type: "door", ok: m.ok === true, message: typeof m.message === "string" ? m.message : null };
    case "ended":
      if (typeof m.end_reason !== "string") return null;
      return { type: "ended", end_reason: m.end_reason as CallEndReason };
    default:
      return null;
  }
}

/** `API_BASE_URL` is e.g. `https://api.nodus.foo/api` (lib/config), like the portal socket's. */
export function callSocketUrl(apiBaseUrl: string, callId: number, token: string): string {
  return `${apiBaseUrl.replace(/^http/i, "ws")}/ws/doorbell/calls/${callId}/?token=${encodeURIComponent(token)}`;
}
